import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import LanguageToggle from '../atoms/LanguageToggle';

interface NavbarProps {
  isSidebarOpen: boolean;
  onToggleSidebar: () => void;
  activeNavLabel?: string;
  activeNavDescription?: string;
}

export const Navbar: React.FC<NavbarProps> = ({
  isSidebarOpen,
  onToggleSidebar,
  activeNavLabel,
  activeNavDescription,
}) => {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  const roleLabel = user?.role
    ? t(`roles.${user.role}`, { defaultValue: t('roles.fallback') })
    : t('roles.fallback');

  const todayGreeting = React.useMemo(() => {
    const locale = i18n.language === 'id' ? 'id-ID' : 'en-US';
    const formatter = new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
    return formatter.format(new Date());
  }, [i18n.language]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setIsProfileMenuOpen(false);
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur shadow-sm">
      <div className="flex min-h-16 items-center justify-between px-4 lg:px-6">
        {/* Left Section */}
        <div className="flex items-center gap-3">
          <button
            className="flex h-10 w-10 items-center justify-center rounded-full text-slate-600 hover:bg-slate-100 transition-colors"
            onClick={onToggleSidebar}
            aria-label={isSidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
          >
            {isSidebarOpen ? (
              <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>

          <div className="hidden flex-col lg:flex">
            <span className="inline-flex rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-indigo-700">
              {todayGreeting}
            </span>
            <p className="mt-1 text-sm font-semibold text-slate-800">
              {user?.name
                ? t('layout.welcomeBack', {
                    name: user.name.split(' ')[0],
                    defaultValue: 'Welcome back, {{name}}!',
                  })
                : t('layout.welcomeGeneric', {
                    defaultValue: 'Manage your clinic with confidence.',
                  })}
            </p>
          </div>
        </div>

        {/* Center Section */}
        <div className="hidden flex-1 max-w-xl lg:flex lg:px-8">
          <div className="relative w-full">
            <input
              type="search"
              placeholder={t('layout.searchPlaceholder', {
                defaultValue: 'Search patients, invoices...',
              })}
              className="w-full rounded-full border border-slate-300 px-4 py-2 pr-16 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            />
            <kbd className="absolute right-3 top-1/2 -translate-y-1/2 rounded bg-slate-100 px-2 py-1 text-xs text-slate-500">⌘K</kbd>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-2">
          <a href="/ai-chat-full" className="flex items-center gap-2 rounded-full px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 transition-colors">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <rect height="10" rx="2" width="18" x="3" y="11" />
              <circle cx="12" cy="5" r="2" />
              <path d="M12 7v4" />
              <line x1="8" x2="8.01" y1="16" y2="16" />
              <line x1="16" x2="16.01" y1="16" y2="16" />
            </svg>
            <span className="hidden sm:inline">AI</span>
          </a>

          <button className="flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors shadow-sm">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t('layout.newAppointment', { defaultValue: 'New' })}
          </button>

          <div className="relative" ref={profileMenuRef}>
            <button
              className="flex items-center gap-2 rounded-full border border-slate-200 px-3 py-2 hover:bg-slate-50 transition-colors"
              onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white">
                <span className="text-xs font-semibold">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
              </div>
              <div className="hidden text-left lg:block">
                <div className="text-sm font-semibold text-slate-800">{user?.name || 'User'}</div>
                <div className="text-xs text-slate-500">{roleLabel}</div>
              </div>
              <svg
                className={`h-4 w-4 text-slate-400 transition-transform ${isProfileMenuOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 20 20"
              >
                <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {isProfileMenuOpen && (
              <div className="absolute right-0 top-full z-50 mt-2 w-64 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
                <div className="flex items-center gap-3 px-3 py-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-600 text-white">
                    <span className="text-sm font-semibold">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-800">{user?.name || 'User'}</p>
                    <p className="text-xs text-slate-500">{roleLabel}</p>
                  </div>
                </div>

                <div className="my-2 h-px bg-slate-200"></div>

                <div className="rounded-lg bg-slate-50 p-3 mx-2">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t('layout.profileMenu.language')}
                  </p>
                  <LanguageToggle />
                </div>

                <div className="my-2 h-px bg-slate-200"></div>

                <button
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  onClick={handleLogout}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" strokeLinecap="round" strokeLinejoin="round" />
                    <polyline points="16 17 21 12 16 7" strokeLinecap="round" strokeLinejoin="round" />
                    <line x1="21" x2="9" y1="12" y2="12" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {t('common.logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Search */}
      <div className="border-t border-slate-200 px-4 py-3 lg:hidden">
        <div className="relative">
          <input
            type="search"
            placeholder={t('layout.searchPlaceholder', { defaultValue: 'Search...' })}
            className="w-full rounded-full border border-slate-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
          />
        </div>
      </div>

      {/* Active Section Info */}
      {(activeNavLabel || activeNavDescription) && (
        <div className="border-t border-slate-200 bg-slate-50 px-4 py-2">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {activeNavLabel && (
              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-3 py-1 font-semibold text-indigo-700">
                <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                {activeNavLabel}
              </span>
            )}
            {activeNavDescription && (
              <span className="text-slate-500">{activeNavDescription}</span>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
